<template>
  <div>
    <v-container v-if="!horse.id && $apollo.queries.horse.loading">
      <AppLoadingPage />
    </v-container>
    <div v-else-if="notFound">
      <NoPage custom-text="Could not get the horse from server" />
    </div>
    <v-container v-else data-test="horseProfile" class="horse-profile-page">
      <v-progress-linear v-if="$apollo.queries.horse.loading" indeterminate />

      <v-row style="position: relative; height: 100%">
        <v-col cols="12" md="8" lg="6">
          <v-card tile class="pa-4 transparent-border" style="height: 100%">
            <v-row dense style="height: 100%">
              <v-col cols="12" sm="4" class="mt-2 text-center">
                <div class="pa-5">
                  <img
                    :src="horseLogo"
                    aria-hidden="true"
                    style="max-width: 100%"
                  />
                </div>

                <div class="mt-3 text-center chip-smaller">
                  <AppChip v-if="horse.isVisiting" small color="accent">
                    {{ $t("horses:horseProfile.horseIsVisiting.badge") }}
                  </AppChip>
                  <AppChip
                    v-else-if="horse.belongsToRidingSchool"
                    color="accent"
                    small
                  >
                    {{
                      $t("horses:horseProfile.horseIsRidingSchoolHorse.badge")
                    }}
                  </AppChip>
                </div>
              </v-col>

              <v-col
                cols="12"
                sm="8"
                class="pb-0 d-flex flex-column"
                style="position: relative"
              >
                <h3 style="font-size: 18px">
                  {{ horse.name }}
                  <span style="font-size: 14px; font-weight: normal">
                    {{ " | " + horse.ageForSeason }}
                    {{
                      $t(
                        "horses:horseProfilePage.years.abrv.secondarytext",
                        "y",
                        "Abrv for years"
                      )
                    }}
                  </span>
                </h3>

                <div v-if="horse.birthName != horse.name" class="text-muted">
                  {{ horse.birthName }}
                </div>

                <div v-if="horse.ponyType" class="my-3">
                  {{ $t("horses:ponyTypes." + horse.ponyType) }}
                  <span v-if="horse.currentPonyMeasurement">{{
                    horse.currentPonyMeasurement.height + "cm"
                  }}</span>
                  <AppShowHorsePonyMeasurementStatus :horse="horse" />
                </div>

                <div v-if="federation === 'FI'" class="mt-4">
                  {{
                    $t(
                      "horses:horseProfilePage.Regnr.field.label",
                      "Reg nr:",
                      "Reg nr field label on horse profile"
                    )
                  }}
                  {{ horse.identifier }}
                </div>
                <div v-if="federation === 'FI'" data-test="heppa">
                  {{
                    $t(
                      "horses:horseProfilePage.externalNr.field.label",
                      "HEPPA ID:",
                      "External reg nr field label on horse profile"
                    )
                  }}
                  {{ horse.identifierHeppa }}
                  <AppValidationAlert
                    v-if="horse.identifierHeppa"
                    :check-valid="horse.identifierHeppaStatus == 'active'"
                    :valid="
                      $t(
                        'horses:HorseProfilePage.identifier.Active.label',
                        'Active',
                        'Label for active identifier status'
                      )
                    "
                    :invalid="
                      $t(
                        'horses:HorseProfilePage.identifier.Inactive.label',
                        'Inactive',
                        'Label for inactive identifier status'
                      )
                    "
                  />
                </div>

                <div
                  class="mt-4 d-flex"
                  data-test="currentSeasonLicence"
                  style="align-items: center"
                >
                  {{
                    $t(
                      "horses:horseProfilePage.sportLicence.field.label",
                      "Sport licence",
                      "Sport licence field label on horse profile"
                    )
                  }}
                  <AppValidationAlert
                    :check-valid="horse.currentSeasonLicenceValid"
                    :valid="
                      $t(
                        'horses:appshow.licence.valid.label',
                        'Valid',
                        'Label for valid yearly licence status'
                      )
                    "
                    :invalid="
                      $t(
                        'horses:appshow.licence.invalid.label',
                        'Not valid',
                        'Label for invalid yearly licence status'
                      )
                    "
                    class="mx-2"
                  />
                  <AppShowSuspension
                    v-if="horse.suspended"
                    :suspension-reason="horse.suspensionReason"
                    class="mx-2"
                  />
                </div>
                <div data-test="licenceNumber">
                  {{
                    $t(
                      "horses:horseProfilePage.InternalLicence.field.label",
                      "KIPA licence:",
                      "Internal licence field label on horse profile"
                    )
                  }}
                  {{ horse.licenceNumber }}
                  <AppValidationAlert
                    v-if="horse.licenceNumber"
                    :check-valid="horse.licenceNumberStatus == 'active'"
                    :valid="
                      $t(
                        'horses:HorseProfilePage.InternalLicence.Active.label',
                        'Active',
                        'Label for active internal licence status'
                      )
                    "
                    :invalid="
                      $t(
                        'horses:HorseProfilePage.InternalLicence.Inactive.label',
                        'Inactive',
                        'Label for inactive internal licence status'
                      )
                    "
                  />
                </div>
                <div data-test="feiLicence" class="my-2">
                  <div>
                    {{
                      $t(
                        "horses:horseProfilePage.FEIlicence.field.label",
                        "FEI licence:",
                        "FEI licence field label on horse profile"
                      )
                    }}
                    <v-tooltip
                      :disabled="!Boolean(horse.feiPassportExpirationDate)"
                      location="bottom"
                    >
                      <template #activator="{ props }">
                        <span v-bind="props"> &nbsp;{{ horse.feiid }} </span>
                      </template>
                      <span>
                        {{ $t("horses:horseProfilePage.feiid.tooltip") }}
                        {{ $dayjs.date(horse.feiPassportExpirationDate) }}
                      </span>
                    </v-tooltip>
                  </div>
                  <AppShowFEILicences :licences="horse.sportLicences">
                    <template #label>
                      {{
                        $t(
                          "horses:horsePage.FEIRegistration.field",
                          "FEI registration"
                        )
                      }}
                    </template>
                  </AppShowFEILicences>
                </div>
                <div v-if="horse.ueln">
                  {{ $t("horses:horseProfilePage.ueln.field.label", "UELN:") }}
                  {{ horse.ueln }}
                </div>
                <div v-if="horse.passportNumber">
                  {{
                    $t(
                      "horses:horseProfilePage.passportNumber.field.label",
                      "Passport:"
                    )
                  }}
                  {{ horse.passportNumber }}
                </div>
                <div v-if="horse.passportDocumentIssuingBodyId">
                  {{
                    $t(
                      "horses:horseProfilePage.issuingBody.field.label",
                      "Issuing body:"
                    )
                  }}
                  {{ horse.passportDocumentIssuingBody.name }}
                </div>
                <div v-if="horse.chipNumber">
                  {{
                    $t(
                      "horses:horseProfilePage.chipNumber.field.label",
                      "Chip:"
                    )
                  }}
                  {{ horse.chipNumber }}
                </div>
                <template v-if="federation === 'FI'">
                  <div class="mt-4">
                    {{
                      $t(
                        "horses:horseProfilePage.FEIFID.field.label",
                        "FEIF ID:",
                        "FEIF ID field label on horse profile"
                      )
                    }}
                    {{ horse.icelandicRegistryNumber }}
                  </div>
                  <div>
                    {{
                      $t(
                        "horses:horseProfilePage.WesternID.field.label",
                        "Western ID:",
                        "Western ID field label on horse profile"
                      )
                    }}
                    {{ horse.westernRegistryNumber }}
                  </div>
                </template>

                <template
                  v-if="
                    $allowedMutation(horse.allowedFields, 'lastVaccination')
                  "
                >
                  <div class="mt-4">
                    {{
                      $t(
                        "horses:horseProfilePage.vaccination.field.label",
                        "Vaccination (national rule):",
                        "Vaccination field label on horse profile"
                      )
                    }}
                    <AppShowHorseVaccinationStatus :horse="horse" />
                  </div>
                  <div>
                    {{
                      $t(
                        "horses:horseProfilePage.vaccinationInternational.field.label",
                        "Vaccination (international rule):",
                        "Vaccination field label on horse profile for international rule"
                      )
                    }}
                    <AppShowHorseVaccinationStatus
                      :horse="horse"
                      rule="international"
                    />
                  </div>
                </template>

                <div
                  v-show="
                    $auth.checkUserCanPerformMutationsInScopeAny(
                      'sanctionCreate'
                    ) ||
                    licencePurchasable ||
                    $allowedMutation(horse.allowedMutations, [
                      'horseApplicationFeiProduct',
                      'horseCreateInFei',
                      'horseApplicationSportLicence'
                    ])
                  "
                  class="text-center text-md-right mt-auto pt-4"
                  style="justify-content: end"
                >
                  <v-row
                    :no-gutters="$vuetify.display.lgAndUp"
                    class="profile-btns__wrapper"
                  >
                    <v-col md="6" class="profile-btns">
                      <v-btn
                        v-if="
                          $allowedMutation(
                            horse.allowedMutations,
                            'horseApplicationFeiProduct'
                          )
                        "
                        class="profile-btn"
                        :to="{
                          name: 'HorseApplicationFEIPassport',
                          params: { horseId: horseId }
                        }"
                        color="primary"
                        dark
                        data-test="FEIPassport"
                      >
                        {{
                          $t(
                            "horses:horseProfilePage.FEIpassport.button.label",
                            "FEI passport"
                          )
                        }}
                      </v-btn>
                    </v-col>
                    <v-col md="6" class="profile-btns">
                      <v-tooltip v-if="licencePurchasable" bottom>
                        <template #activator="{ props: attrs }">
                          <v-btn
                            v-bind="attrs"
                            class="profile-btn"
                            color="primary"
                            data-test="purchasesLicence"
                            @click="() => (showLicenceDialog = true)"
                          >
                            {{
                              $t(
                                "horses:HorseProfilePage.button.BuyLicence.label",
                                "Buy licence",
                                "Label for horse licence purchases"
                              )
                            }}
                          </v-btn>
                        </template>
                        <span>
                          {{
                            $t(
                              "horses:HorseProfilePage.button.BuyLicence.tooltip",
                              "Buy licence",
                              "Tooltip for horse licence purchases"
                            )
                          }}
                        </span>
                      </v-tooltip>

                      <v-btn
                        v-if="
                          $allowedMutation(
                            horse.allowedMutations,
                            'horseApplicationSportLicence'
                          )
                        "
                        class="profile-btn"
                        color="primary"
                        data-test="horseApplicationSportLicense"
                        :to="{
                          name: 'HorseApplicationSportLicence',
                          params: { horseId: horse.id }
                        }"
                      >
                        <span
                          :style="[
                            horseApplicationSportLicenseText.length > 20
                              ? 'font-size: 11px;'
                              : ''
                          ]"
                          >{{ horseApplicationSportLicenseText }}</span
                        >
                      </v-btn>

                      <v-menu
                        v-if="
                          $allowedMutation(horse.allowedMutations, [
                            'horseCreateInFei'
                          ]) ||
                          $auth.checkUserCanPerformMutationsInScopeAny(
                            'sanctionCreate'
                          )
                        "
                      >
                        <template #activator="{ props: attrs }">
                          <v-btn
                            color="edit"
                            v-bind="attrs"
                            data-test="horseActionsMenu"
                            :class="['profile-btn text-white']"
                          >
                            {{
                              $t(
                                "horse:HorseProfilePage.button.actions.label",
                                "Actions"
                              )
                            }}
                          </v-btn>
                        </template>

                        <v-list>
                          <v-list-item
                            v-if="
                              $allowedMutation(
                                horse.allowedMutations,
                                'horseCreateInFei'
                              )
                            "
                            data-test="createHorseInFei"
                            @click="openCreateHorseInFei"
                          >
                            {{
                              $t(
                                "horses:horseProfilePage.horseCreateInFei.button",
                                "Get FEI ID"
                              )
                            }}
                          </v-list-item>

                          <v-list-item
                            v-if="
                              $auth.checkUserCanPerformMutationsInScopeAny(
                                'sanctionCreate'
                              )
                            "
                            data-test="issueSanction"
                            @click="() => $refs.sanctionDialog.openDialog()"
                          >
                            {{
                              $t(
                                "horses:HorseProfilePage.button.issueSanction.label",
                                "Issue a sanction"
                              )
                            }}
                          </v-list-item>

                          <v-list-item
                            v-if="
                              $allowedMutation(
                                horse.allowedMutations,
                                'horseMerge'
                              )
                            "
                            :to="{
                              name: 'HorseDuplicateMerge',
                              query: { horseId }
                            }"
                            target="_blank"
                            ><span class="text-black">{{
                              $t(
                                "horses:common.mergeDuplicates.button",
                                "Merge duplicates"
                              )
                            }}</span>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" lg="3" data-test="background">
          <v-card
            tile
            class="pa-4 transparent-border d-flex"
            style="height: 100%; flex-direction: column"
          >
            <h3 style="font-size: 18px">
              {{ $t("horses:HorseProfile.background.heading", "Background") }}
            </h3>

            <div class="my-3">
              <div>
                {{
                  $t(
                    "horses:horseProfilePage.Birthdate.field.label",
                    "Birth date:",
                    "Birth date field label on horse profile"
                  )
                }}&nbsp;{{ $dayjs.date(horse.dateOfBirth) }}
              </div>
              <div>
                {{
                  $t(
                    "horses:horseProfilePage.Countryofbirth.field.label",
                    "Country of birth:",
                    "Country of birth field label on horse profile"
                  )
                }}
                {{ get(horse.birthCountry, "ioc") }}
                <span
                  :class="
                    'flag-icon fi-' +
                    get(horse.birthCountry, 'alpha2', '').toLowerCase()
                  "
                />
              </div>
            </div>
            <div class="mt-4">
              {{
                $t(
                  "horses:horseProfilePage.owner.field.label",
                  "Owner:",
                  "Owner field label on horse profile"
                )
              }}
              {{ horse.ownerName }}
            </div>
            <div>
              {{
                $t(
                  "horses:horseProfilePage.breed.field.label",
                  "Breed:",
                  "Breed field label on horse profile"
                )
              }}
              {{ studbookOrBreed }}
            </div>
            <div>
              {{
                $t(
                  "horses:horseProfilePage.breeder.field.label",
                  "Breeder",
                  "Breeder field label on horse profile"
                )
              }}
              {{ horse.breederName }}
            </div>

            <div class="mt-4">
              {{
                $t(
                  "horses:horseProfilePage.gender.field.label",
                  "Gender:",
                  "Gender field label on horse profile"
                )
              }}
              {{ $t("common:horse.gender." + horse.gender, horse.gender) }}
            </div>
            <div>
              {{
                $t(
                  "horses:horseProfilePage.color.field.label",
                  "Color:",
                  "Color field label on horse profile"
                )
              }}
              {{ colorName }}
            </div>
            <div v-if="horse.burnmarkDesc">
              {{
                $t(
                  "horses:horseProfilePage.horseBurnmarkDesc.field.label",
                  "Burn mark:"
                )
              }}
              {{ horse.burnmarkDesc }}
            </div>
            <div
              class="mt-auto pt-4 text-center text-md-right"
              style="align-self: end"
            >
              <v-btn
                v-if="
                  $allowedMutation(
                    horse.allowedMutations,
                    'horseApplicationOwnershipChange'
                  )
                "
                class="profile-btn"
                color="primary"
                :to="{
                  name: 'HorseApplicationOwnershipChange',
                  params: { horseId: horseId }
                }"
                style="
                  width: 150px;
                  height: 30px !important;
                  min-height: 30px !important;
                "
                data-test="ownerChange"
              >
                <span
                  :style="
                    applySmallerFontForButtons(
                      $t(
                        'horses:horseProfilePage.ownershipSection.changeOwnerButton.label',
                        'Change owner'
                      )
                    )
                      ? 'font-size: 8px'
                      : ''
                  "
                >
                  {{
                    $t(
                      "horses:horseProfilePage.ownershipSection.changeOwnerButton.label",
                      "Change owner"
                    )
                  }}
                </span>
              </v-btn>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="3" data-test="pedigree">
          <v-card
            tile
            class="pa-4 transparent-border d-flex flex-column"
            style="height: 100%"
          >
            <h3 style="font-size: 18px">
              {{
                $t(
                  "horses:horseProfilePage.pedigree.subheading",
                  "Pedigree",
                  "Pedigree subheading label on horse profile"
                )
              }}
            </h3>
            <div class="my-3">
              <div>
                {{
                  $t(
                    "horses:horseProfilePage.sire.field.label",
                    "Sire:",
                    "Sire field label on horse profile"
                  )
                }}
                <template v-if="horse.sireId">
                  <router-link
                    :to="{
                      name: 'HorseProfilePage',
                      params: { horseId: horse.sireId }
                    }"
                  >
                    {{ horse.sireName }}
                  </router-link>
                </template>
                <template v-else>
                  {{ horse.sireName }}
                </template>
              </div>
              <div v-if="horse.sireSireName">
                {{
                  $t(
                    "horses:horseProfilePage.sireSire.field.label",
                    "Sire of sire:"
                  )
                }}
                {{ horse.sireSireName }}
              </div>
              <div>
                {{
                  $t(
                    "horses:horseProfilePage.dam.field.label",
                    "Dam:",
                    "Dam field label on horse profile"
                  )
                }}
                <template v-if="horse.damId">
                  <router-link
                    :to="{
                      name: 'HorseProfilePage',
                      params: { horseId: horse.damId }
                    }"
                  >
                    {{ horse.damName }}
                  </router-link>
                </template>
                <template v-else>
                  {{ horse.damName }}
                </template>
              </div>
              <div>
                {{
                  $t(
                    "horses:horseProfilePage.damsire.field.label",
                    "Damsire",
                    "Damsire field label on horse profile"
                  )
                }}
                {{ horse.damSireName }}
              </div>
              <div v-if="horse.damDamName">
                {{
                  $t(
                    "horses:horseProfilePage.damOdDam.field.label",
                    "Dam of dam"
                  )
                }}
                {{ horse.damDamName }}
              </div>
            </div>

            <div style="flex-grow: 1" />
            <AppEntityNote
              v-if="
                horse.allowedFields.includes('internalNote') &&
                horse.allowedMutations.includes('horseCreateOrUpdate')
              "
              entity-type="HORSE"
              :entity="horse"
            />
          </v-card>
        </v-col>
      </v-row>

      <div class="mb-5 text-center text-md-right">
        <template
          v-if="$allowedMutation(horse.allowedMutations, 'horseCreateOrUpdate')"
        >
          <v-btn
            :to="{ name: 'HorseEdit', params: { horseId: horse.id } }"
            style="min-width: 225px; border-radius: 20px"
            class="mb-2 mt-2"
            color="accent"
          >
            {{ $t("common:button.edit", "Edit profile", "Edit profile") }}
          </v-btn>
          <br />
        </template>
        <small class="text-muted">
          {{
            $t(
              "common:lastupdated.secondarytext",
              "Last updated:",
              "Last updated secondary text"
            )
          }}
          {{ $dayjs.date(horse.updatedAt, "fullTime") }}
        </small>
      </div>

      <div>
        <v-tabs v-model="tabSelected" class="fipa-tabs">
          <v-tab
            value="#results"
            href="#results"
            data-test="#results"
            class="bolded-text"
          >
            {{ $t("horses:horseProfilePage.results.tab.heading", "Results") }}
          </v-tab>

          <v-tab
            v-if="horse.allowedFields.includes('upcomingRegistrations')"
            value="#upcoming"
            href="#upcoming"
            data-test="#upcoming"
            class="bolded-text"
          >
            {{ $t("horses:horseProfilePage.upcoming.tab.heading", "Upcoming") }}
          </v-tab>

          <v-tab
            v-if="
              horse.allowedFields.includes('reports') &&
              horse.reports.length > 0
            "
            value="#reports"
            href="#reports"
            data-test="#reports"
            class="bolded-text"
          >
            {{ $t("horses:horseProfilePage.reports.tab.heading", "Reports") }}
          </v-tab>

          <v-tab
            v-if="
              horse.allowedFields.includes('sanctions') &&
              horse.sanctions.length > 0
            "
            value="#sanctions"
            href="#sanctions"
            data-test="#sanctions"
            class="bolded-text"
          >
            {{ $t("horses:HorseProfilePage.Sanctions.tabTitle", "Sanctions") }}
          </v-tab>

          <v-tab
            value="#licences"
            href="#licences"
            data-test="horseLicencesTab"
            class="bolded-text"
          >
            {{ $t("horses:horseProfilePage.licences.tab.heading", "Licences") }}
          </v-tab>

          <v-tab
            v-if="showVaccinationsTab"
            value="#vaccinations"
            href="#vaccinations"
            data-test="horseVaccinations"
            class="bolded-text"
          >
            {{
              $t(
                "horses:horseProfilePage.vaccinations.tab.heading",
                "Vaccinations"
              )
            }}
          </v-tab>

          <v-tab
            v-if="showMedicalTreatmentsTab"
            value="#medicalTreatments"
            href="#medicalTreatments"
            data-test="medicalTreatments"
            class="bolded-text"
          >
            {{
              $t("horses:horseProfilePage.carebook.tab.heading", "Care book")
            }}
          </v-tab>

          <v-tab
            value="#ponyMeasurements"
            href="#ponyMeasurements"
            data-test="horseMeasurements"
            class="bolded-text"
          >
            {{
              $t(
                "horses:horseProfilePage.ponyMeasurements.tab.heading",
                "Pony measurements"
              )
            }}
          </v-tab>

          <v-tab
            v-if="showHorseOwners"
            value="#owners"
            href="#owners"
            data-test="horseOwners"
            class="bolded-text"
          >
            {{ $t("horses:horseProfilePage.ownershipSection.title", "Owners") }}
          </v-tab>

          <v-tab
            v-if="
              $allowedMutation(horse.allowedMutations, [
                'horseCreateOrUpdate',
                'horseDocumentAdd',
                'horseDocumentDelete'
              ])
            "
            value="#documents"
            href="#documents"
            data-test="horseDocuments"
            class="bolded-text"
          >
            {{
              $t("horses:horseProfilePage.documentSection.title", "Documents")
            }}
          </v-tab>

          <v-tab
            v-if="showQualificationsTab"
            value="#qualifications"
            href="#qualifications"
            data-test="qualifications"
            class="bolded-text"
          >
            {{
              $t(
                "horses:horseProfilePage.qualifications.title",
                "Qualifications"
              )
            }}
          </v-tab>

          <v-tab
            v-if="horse.allowedFields.includes('competingDifficultyLevels')"
            value="#competingDifficultyLevels"
            href="#competingDifficultyLevels"
            data-test="competingDifficultyLevels"
            class="bolded-text"
          >
            {{
              $t(
                "horses:horseProfilePage.competingDifficultyLevels.tab.heading",
                "Levels"
              )
            }}
          </v-tab>

          <v-tab
            v-if="allDuplicates.length > 0"
            value="#duplicates"
            href="#duplicates"
            data-test="duplicates"
            class="bolded-text"
          >
            {{
              $t("horses:horseProfilePage.duplicates.tab.heading", "Duplicates")
            }}
          </v-tab>
        </v-tabs>

        <AppTabItems ref="validTabs" v-model="tabSelected">
          <AppTabItem value="#results">
            <HorseCompetitionResults :horse="horse" />
          </AppTabItem>

          <AppTabItem
            v-if="horse.allowedFields.includes('upcomingRegistrations')"
            value="#upcoming"
          >
            <HorseUpcomingEvents :horse-id="horseId" />
          </AppTabItem>

          <AppTabItem
            v-if="
              horse.allowedFields.includes('reports') &&
              horse.reports.length > 0
            "
            value="#reports"
          >
            <HorseReports :horse="horse" />
          </AppTabItem>

          <AppTabItem
            v-if="
              horse.allowedFields.includes('sanctions') &&
              horse.sanctions.length
            "
            value="#sanctions"
          >
            <HorseSanctions :horse="horse" />
          </AppTabItem>

          <AppTabItem value="#licences">
            <HorseLicences
              :horse-id="horseId"
              :allowed-mutations="horse.allowedMutations"
              data-test="horseLicences"
              @open-purchases="() => (showLicenceDialog = true)"
              @refetch="refetch"
            />
          </AppTabItem>
          <AppTabItem v-if="showVaccinationsTab" value="#vaccinations">
            <HorseVaccinationsSection
              :horse-id="horse.id"
              :can-add="
                $allowedMutation(horse.allowedMutations, 'horseAddVaccination')
              "
            />
            <VaccinationChecks
              v-if="$allowedMutation(horse.allowedFields, 'vaccinationChecks')"
              :horse="horse"
              class="mt-10"
            />
          </AppTabItem>

          <AppTabItem
            v-if="showMedicalTreatmentsTab"
            value="#medicalTreatments"
          >
            <HorseMedicalTreatments :horse="horse" />
          </AppTabItem>

          <AppTabItem value="#ponyMeasurements">
            <HorsePonyMeasurementsSection
              :horse="horse"
              @update="$apollo.queries.horse.refetch()"
            />
          </AppTabItem>

          <AppTabItem
            v-if="showHorseOwners"
            data-test="horseOwners"
            value="#owners"
          >
            <HorseOwners :horse-id="horseId" />
          </AppTabItem>

          <AppTabItem value="#documents">
            <HorseDocuments :horse-id="horseId" />
          </AppTabItem>

          <AppTabItem v-if="showQualificationsTab" value="#qualifications">
            <ProfileQualificationsTab :horse-id="horseId" />
          </AppTabItem>

          <AppTabItem value="#competingDifficultyLevels">
            <AppCompetingLevels :horse-id="horseId" />
          </AppTabItem>

          <AppTabItem v-if="allDuplicates.length > 0" value="#duplicates">
            <HorseDuplicatesSection
              :horse-id="horse.id"
              :duplicate-horses="allDuplicates"
              @changed="$apollo.queries.findDuplicateHorses.refetch()"
            />
          </AppTabItem>
        </AppTabItems>
      </div>

      <HorseLicencePurchasesForm
        v-if="licencePurchasable"
        v-model="showLicenceDialog"
        :horse="horse"
      />
      <HorseManualFeiForm
        v-if="$allowedMutation(horse.allowedMutations, 'horseCreateInFei')"
        ref="horseManualFeiForm"
        :horse="horse"
      />

      <SanctionDialog
        v-if="$auth.checkUserCanPerformMutationsInScopeAny('sanctionCreate')"
        ref="sanctionDialog"
        sanctioned-entity="Horse"
        refetch-query-name="horse"
        :values="{ sanctionedHorseId: horse.id }"
        @open-sanction-tab="openSanction"
      />
    </v-container>
  </div>
</template>

<script>
import gql from "fraql";
import { upperFirst, get } from "lodash-es";
import { ref } from "vue";

import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppValidationAlert from "@elements/AppValidationAlert.vue";
import AppShowHorseVaccinationStatus from "@AppShows/horse/AppShowHorseVaccinationStatus.vue";
import AppShowHorsePonyMeasurementStatus from "@AppShows/horse/AppShowHorsePonyMeasurementStatus.vue";
import AppShowSuspension from "@AppShows/AppShowSuspension.vue";
import AppShowFEILicences from "@AppShows/AppShowFEILicences.vue";

import HorsePonyMeasurementsSection from "@/components/horse/HorsePonyMeasurementsSection.vue";
import HorseCompetitionResults from "@/components/horse/HorseCompetitionResults.vue";
import HorseUpcomingEvents from "@/components/horse/HorseUpcomingEvents.vue";
import HorseDuplicatesSection from "@/components/horse/HorseDuplicatesSection.vue";
import HorseVaccinationsSection from "@/components/horse/vaccinations/HorseVaccinationsSection.vue";
import HorseLicences from "@/components/horse/HorseLicences.vue";
import HorseOwners from "@/components/horse/HorseOwners.vue";
import HorseDocuments from "@/components/horse/HorseDocuments.vue";
import HorseLicencePurchasesForm from "./HorseLicencePurchasesForm.vue";
import HorseReports from "./HorseReports.vue";
import HorseSanctions from "./HorseSanctions.vue";
import AppCompetingLevels from "@elements/competing/AppCompetingLevels.vue";
import HorseMedicalTreatments from "./medicalTreatments/HorseMedicalTreatments.vue";
import NoPage from "@/components/error_pages/404.vue";

import ProfileQualificationsTab from "@elements/qualifications/ProfileQualificationsTab.vue";
import SanctionDialog from "@elements/sanctions/SanctionDialog.vue";
import AppEntityNote from "@elements/AppEntityNote.vue";
import { useHorseHelper } from "@composables/horse_helper";
import HorseManualFeiForm from "./HorseManualFeiForm.vue";
import FipaListener from "@/utils/listenerUtility";
import { useRouteScroll } from "@composables/route_scroll";
import VaccinationChecks from "./vaccinations/VaccinationChecks.vue";

import { prependApiDomain } from "@/utils/prependApiDomain";
import AppTabItems from "@elements/tab/AppTabItems.vue";
import AppTabItem from "@elements/tab/AppTabItem.vue";
import { useTabNavigationHelper } from "@composables/tabNavigationHelper";
import AppChip from "@elements/AppChip.vue";

export default {
  name: "HorseProfilePage",
  components: {
    AppLoadingPage,
    HorsePonyMeasurementsSection,
    AppValidationAlert,
    HorseVaccinationsSection,
    HorseDuplicatesSection,
    HorseUpcomingEvents,
    AppShowHorseVaccinationStatus,
    AppShowHorsePonyMeasurementStatus,
    HorseCompetitionResults,
    NoPage,
    HorseLicences,
    HorseOwners,
    HorseDocuments,
    HorseLicencePurchasesForm,
    ProfileQualificationsTab,
    HorseReports,
    SanctionDialog,
    HorseSanctions,
    AppShowSuspension,
    AppShowFEILicences,
    AppEntityNote,
    HorseManualFeiForm,
    AppCompetingLevels,
    HorseMedicalTreatments,
    VaccinationChecks,
    AppChip,

    AppTabItems,
    AppTabItem
  },
  props: { horseId: { type: String, default: null } },
  setup() {
    const { tabSelected } = useTabNavigationHelper("#results");
    const { horseColorText, horseBreedOrStudbookText } = useHorseHelper();

    const { scrollIntoElement, scrollToTab } = useRouteScroll();
    const horse = ref({
      id: null,
      allowedMutations: [],
      allowedFields: []
    });

    const findDuplicateHorses = ref([]);
    const notFound = ref(false);
    const showLicenceDialog = ref(false);

    return {
      tabSelected,
      horseColorText,
      horseBreedOrStudbookText,

      horse,
      findDuplicateHorses,
      notFound,
      showLicenceDialog,
      scrollIntoElement,
      scrollToTab
    };
  },
  computed: {
    horseApplicationSportLicenseText() {
      return this.$t(
        "horses:horseApplicationDataUpdate.button",
        "Update horse´s data"
      );
    },
    studbookOrBreed() {
      return this.horseBreedOrStudbookText(this.horse);
    },
    colorName() {
      return this.horseColorText(this.horse);
    },
    horseLogo() {
      if (this.horse.breedingStudbook?.logoPath) {
        return prependApiDomain(this.horse.breedingStudbook.logoPath);
      }
      return this.$federation.placeholderLogo;
    },
    get: () => get,
    upperFirst: () => upperFirst,
    federation() {
      return this.$federation.countryCode;
    },
    showMedicalTreatmentsTab() {
      return this.$allowedMutation(this.horse.allowedFields, [
        "horseMedicalTreatments"
      ]);
    },
    showHorseOwners() {
      return this.$allowedMutation(this.horse.allowedMutations, [
        "horseCreateOrUpdate",
        "horseGenerateOwnershipCertificate"
      ]);
    },
    licencePurchasable() {
      return this.$allowedMutation(this.horse.allowedMutations, [
        "horsePurchaseSeasonSportLicence",
        "horsePurchaseTemporarySportLicence",
        "horseApplicationFeiSeasonLicence",
        "horsePurchaseLifetimeSportLicence"
      ]);
    },
    showQualificationsTab() {
      return this.$allowedMutation(this.horse.allowedFields, [
        "qualificationStatuses"
      ]);
    },
    showVaccinationsTab() {
      return this.$allowedMutation(this.horse.allowedFields, "vaccinations");
    },
    allDuplicates() {
      let duplicates = [];
      if (this.horse.isDuplicateOfHorse) {
        duplicates.push(this.horse.isDuplicateOfHorse);
      }
      if (this.horse.duplicateHorses && this.horse.duplicateHorses.length > 0) {
        duplicates = duplicates.concat(this.horse.duplicateHorses);
      }
      if (this.findDuplicateHorses.length > 0) {
        duplicates = duplicates.concat(this.findDuplicateHorses);
      }
      return duplicates;
    }
  },
  apollo: {
    horse: {
      query: gql`
          query horseProfile ($id: ID!, $placement: String!) {
            horse (id: $id) {
              id
              ${AppShowHorsePonyMeasurementStatus.fragments.horse}
              ${HorsePonyMeasurementsSection.fragments.horse}
              ${AppEntityNote.fragments.horseFields}
              ${HorseCompetitionResults.fragments.horseFields}
              identifier
              activeIdentifier
              identifierHeppa
              identifierHeppaStatus
              identifierHeppaActivatedAt
              identifierHeppaDeactivatedAt
              name
              feiid
              feiPassportExpirationDate
              birthName
              dateOfBirth
              ageForSeason
              birthCountryId
              birthCountry @client {
                id
                name
                ioc
                alpha2
              }
              currentSeasonLicenceValid
              icelandicRegistryNumber
              westernRegistryNumber
              gender
              ownerName
              breederName
              sireName
              sireSireName
              sireId
              damName
              damId
              damSireName
              damDamName
              colorName
              colorId
              color @client {
                id
                name
              }
              colorComplement
              breedingStudbookId
              breedingStudbook @client {
                id
                nameShort
                logoPath
              }
              breedId
              breed @client {
                id
                name
              }
              licenceNumber
              licenceNumberStatus
              licenceNumberActivatedAt
              licenceNumberDeactivatedAt
              horseType
              ponyType
              ueln
              chipNumber
              passportNumber
              passportDocumentIssuingBodyId
              passportDocumentIssuingBody @client {
                id
                name
              }
              burnmarkDesc
              isVisiting
              belongsToRidingSchool
              ${AppShowSuspension.fragments.suspension}
              sportLicences {
                ${AppShowFEILicences.fragments.horseLicenceFields}
              }
              currentSportLicences {
                id
                disciplineId
                discipline @client {
                  id
                  name
                }
                licenceCategoryCodename
              }
              currentPonyMeasurement {
                id
                measurementDate
                effectiveFromDate
                effectiveUntilDate
                height
                resultingType
                horseId
              }

              isDuplicateOfHorse {
                ${HorseDuplicatesSection.fragments.horse}
              }
              duplicateHorses {
                ${HorseDuplicatesSection.fragments.horse}
              }
              reports(placement: $placement) {
                ${HorseReports.fragments.reports}
              }
              sanctions {
                id
              }
              flaggedForManualDuplicateCheck
              allowedFields
              allowedMutations
              createdAt
              updatedAt
            }
          }
        `,
      variables() {
        return { id: this.horseId, placement: "HORSE_PROFILE" };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        if (!data || !data.horse) {
          if (this.notFound) {
            this.$root.$snackbarServerError("Horse was not found", {
              level: "info"
            });
          }
          this.notFound = true;
        } else if (data && data.horse) {
          this.horse = data.horse;
          FipaListener.emit("breadcrumbPageName", {
            name: data.horse.name,
            id: data.horse.id,
            breadcrumbName: "horseName"
          });
          this.scrollToTab();
          this.scrollIntoElement();
        } else {
          this.notFound = true;
        }
      }
    },
    findDuplicateHorses: {
      query: gql`
          query findDuplicateHorses ($horseId: ID!) {
            findDuplicateHorses (horseId: $horseId) {
              id
              ${HorseDuplicatesSection.fragments.horse}
          }}
        `,
      variables() {
        return { horseId: this.horseId };
      },
      skip() {
        return !this.horse || !this.horse.flaggedForManualDuplicateCheck;
      }
    }
  },
  methods: {
    openCreateHorseInFei() {
      this.$refs.horseManualFeiForm.dialogValue = true;
    },
    applySmallerFontForButtons(text) {
      return text.length > 20;
    },
    openSanction() {
      if (this.horse.sanctions.length > 0) {
        this.tabSelected = "#sanctions";
      }
    },
    refetch() {
      this.$apollo.queries.horse.refetch();
    }
  }
};
</script>
